/// <reference types="vite/client" />

/**
 * reCAPTCHA Enterprise access for our own risk assessment.
 *
 * Firebase App Check (see src/firebase.config.ts) already loads
 * enterprise.js and renders an invisible widget of its own, which it re-runs
 * on every token refresh for the life of the tab. Google's reCAPTCHA is a
 * single global runtime shared by everything on the page, so a second library
 * with its own script and its own lifecycle can invalidate App Check's widget
 * and break those refreshes.
 *
 * This module therefore renders our own widget on the runtime App Check
 * already loaded, and deliberately never tears anything down: no removing
 * script tags, no clearing window.___grecaptcha_cfg, no removing containers.
 * App Check owns the first widget; we own ours; neither invalidates the other.
 */

const SCRIPT_URL = 'https://www.google.com/recaptcha/enterprise.js';
const WIDGET_CONTAINER_ID = 'ec_risk_assessment_recaptcha';
const RISK_ASSESSMENT_ACTION = 'risk_assessment';
const RUNTIME_POLL_INTERVAL_MS = 100;
const RUNTIME_TIMEOUT_MS = 10_000;

interface ReCaptchaEnterpriseRuntime {
  ready: (callback: () => void) => void;
  render: (
    container: string | HTMLElement,
    parameters: { sitekey: string; size: 'invisible' }
  ) => number;
  execute: (
    widgetId: number,
    parameters: { action: string }
  ) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: { enterprise?: ReCaptchaEnterpriseRuntime };
  }
}

/**
 * App Check injects enterprise.js during startup, so in practice the tag is
 * already there. This is a fallback for the cases where it isn't (App Check
 * skipped, or we render before it got that far) and is a no-op otherwise, so
 * the page only ever holds one reCAPTCHA script.
 */
const ensureScriptLoaded = (): void => {
  if (window.grecaptcha?.enterprise) {
    return;
  }
  if (document.querySelector(`script[src^="${SCRIPT_URL}"]`)) {
    return;
  }

  const script = document.createElement('script');
  script.src = SCRIPT_URL;
  script.async = true;
  document.head.appendChild(script);
};

/* The script loads asynchronously and we may be called before it resolves. */
const waitForRuntime = (): Promise<ReCaptchaEnterpriseRuntime> =>
  new Promise((resolve, reject) => {
    const deadline = RUNTIME_TIMEOUT_MS / RUNTIME_POLL_INTERVAL_MS;
    let attempts = 0;

    const poll = () => {
      const runtime = window.grecaptcha?.enterprise;
      if (runtime) {
        resolve(runtime);
        return;
      }
      if (++attempts >= deadline) {
        reject(new Error('reCAPTCHA Enterprise runtime is not available.'));
        return;
      }
      setTimeout(poll, RUNTIME_POLL_INTERVAL_MS);
    };

    poll();
  });

const ensureContainer = (): HTMLElement => {
  const existing = document.getElementById(WIDGET_CONTAINER_ID);
  if (existing) {
    return existing;
  }

  const container = document.createElement('div');
  container.id = WIDGET_CONTAINER_ID;
  container.style.display = 'none';
  document.body.appendChild(container);
  return container;
};

const renderWidget = async (): Promise<number> => {
  const siteKey = import.meta.env.VITE_ENTERPRISE_CAPTCHA_KEY;
  if (!siteKey) {
    throw new Error('VITE_ENTERPRISE_CAPTCHA_KEY is not defined.');
  }

  ensureScriptLoaded();
  const runtime = await waitForRuntime();
  const container = ensureContainer();

  return new Promise<number>((resolve, reject) => {
    runtime.ready(() => {
      try {
        resolve(
          runtime.render(container, { sitekey: siteKey, size: 'invisible' })
        );
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  });
};

/**
 * Memoized so repeated mounts reuse a single widget rather than registering a
 * new one each time. Cleared on failure so a retry can render again.
 */
let widgetPromise: Promise<number> | undefined;

const ensureWidget = (): Promise<number> => {
  if (!widgetPromise) {
    widgetPromise = renderWidget().catch((error: unknown) => {
      widgetPromise = undefined;
      throw error;
    });
  }
  return widgetPromise;
};

/**
 * Returns a reCAPTCHA token for the createRiskAssessment cloud function.
 * Tokens are single-use, so this runs a fresh execute on every call.
 */
export const executeRiskAssessmentRecaptcha = async (
  action: string = RISK_ASSESSMENT_ACTION
): Promise<string> => {
  const widgetId = await ensureWidget();
  const runtime = await waitForRuntime();
  return runtime.execute(widgetId, { action });
};
